import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { Check } from 'lucide-react';
import Link from 'next/link';
import { buttonVariants } from '@/shadcn/components/Button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shadcn/components/Accordion';
import { FREE_SEAT, CREATOR_SEAT, GROWTH_SEAT, ENTERPRISE_SEAT } from '@/config/monetization';
import PricingButton from './Button';
import { formatNumber } from '@/common/utils/strings';
import { getPlanNickname } from '@/utils/subscription';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

const PricingTable = _ref => {
  let {
    className = '',
    team,
    redirectToBilling,
    redirectToCheckout
  } = _ref;
  const subscriptionType = team ? getPlanNickname(team) : ''; // Check if free plan is active

  const isFree = useMemo(() => subscriptionType === FREE_SEAT.nickname, [subscriptionType]); // Check if starter plan is active

  const isStarter = useMemo(() => subscriptionType === CREATOR_SEAT.nickname, [subscriptionType]); // Check if growth plan is active

  const isGrowth = useMemo(() => subscriptionType === GROWTH_SEAT.nickname, [subscriptionType]); // Check if enterprise plan is active

  const isEnterprise = useMemo(() => subscriptionType === ENTERPRISE_SEAT.nickname, [subscriptionType]);
  const handleRedirectToBilling = useCallback(evt => {
    evt === null || evt === void 0 ? void 0 : evt.preventDefault();
    redirectToBilling();
  }, [redirectToBilling]);
  return /*#__PURE__*/_jsxs("div", {
    className: clsx('mx-auto grid max-w-screen-sm rounded-md border text-center lg:max-w-none lg:grid-cols-4 lg:border-x-0 lg:border-y', className),
    children: [/*#__PURE__*/_jsx("p", {
      className: clsx(isFree && 'bg-gray-200/50', 'px-6 pb-2 pt-6 text-3xl mb-0 font-semibold lg:order-1'),
      children: FREE_SEAT.title
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isFree && 'bg-gray-200/50', 'p-6 lg:order-9'),
      children: [/*#__PURE__*/_jsxs("div", {
        className: "mb-4 flex justify-center",
        children: [/*#__PURE__*/_jsx("span", {
          className: "text-lg font-semibold",
          children: "$"
        }), /*#__PURE__*/_jsx("span", {
          className: "text-6xl font-semibold",
          children: FREE_SEAT.price
        })]
      }), /*#__PURE__*/_jsxs("p", {
        className: "text-sm text-muted-foreground",
        children: [formatNumber(FREE_SEAT.viewLimit), " views per month"]
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: clsx(isFree && 'bg-gray-200/50', 'p-6 lg:order-[13]'),
      children: /*#__PURE__*/_jsx(PricingButton, {
        hasActiveSession: Boolean(team),
        hasActiveSubscription: Boolean(team === null || team === void 0 ? void 0 : team.subscriptionActive),
        currentSubscriptionType: subscriptionType,
        subscriptionPlan: FREE_SEAT,
        signUpLinkText: 'Get Started',
        redirectToCheckout: redirectToCheckout
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isFree && 'bg-gray-200/50', 'border-b p-6 text-left lg:order-[17] lg:border-b-0 lg:border-t'),
      children: [/*#__PURE__*/_jsx("div", {
        className: "hidden lg:block",
        children: /*#__PURE__*/_jsx("ul", {
          className: "mb-4 space-y-4",
          children: FREE_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
            className: "flex items-center gap-2",
            children: [/*#__PURE__*/_jsx(Check, {
              className: "size-4"
            }), /*#__PURE__*/_jsx("span", {
              children: feature
            })]
          }, index))
        })
      }), /*#__PURE__*/_jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "lg:hidden",
        children: /*#__PURE__*/_jsxs(AccordionItem, {
          value: "item-1",
          className: "border-b-0",
          children: [/*#__PURE__*/_jsx(AccordionTrigger, {
            children: "See what's included"
          }), /*#__PURE__*/_jsx(AccordionContent, {
            children: /*#__PURE__*/_jsx("ul", {
              className: "mb-4 space-y-4",
              children: FREE_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
                className: "flex items-center gap-2",
                children: [/*#__PURE__*/_jsx(Check, {
                  className: "size-4"
                }), /*#__PURE__*/_jsx("span", {
                  children: feature
                })]
              }, index))
            })
          })]
        })
      })]
    }), /*#__PURE__*/_jsx("p", {
      className: clsx(isStarter && 'bg-gray-200/50', 'bg-muted px-6 pb-2 pt-6 text-3xl mb-0 font-semibold lg:order-2 capitalize'),
      children: CREATOR_SEAT.title
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isStarter && 'bg-gray-200/50', 'p-6 lg:order-10'),
      children: [/*#__PURE__*/_jsxs("div", {
        className: "mb-4 flex justify-center",
        children: [/*#__PURE__*/_jsx("span", {
          className: "text-lg font-semibold",
          children: "$"
        }), /*#__PURE__*/_jsx("span", {
          className: "text-6xl font-semibold",
          children: CREATOR_SEAT.price
        })]
      }), /*#__PURE__*/_jsxs("p", {
        className: "text-sm text-muted-foreground",
        children: [formatNumber(CREATOR_SEAT.viewLimit), " views / month"]
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isStarter && 'bg-gray-200/50', 'p-6 lg:order-[13]'),
      children: [/*#__PURE__*/_jsx(PricingButton, {
        hasActiveSession: Boolean(team),
        hasActiveSubscription: Boolean(team === null || team === void 0 ? void 0 : team.subscriptionActive),
        currentSubscriptionType: subscriptionType,
        subscriptionPlan: CREATOR_SEAT,
        redirectToCheckout: redirectToCheckout
      }), team && !isFree && /*#__PURE__*/_jsxs("p", {
        className: "mt-4 text-muted-foreground",
        children: [isStarter && /*#__PURE__*/_jsx("a", {
          href: "#",
          className: "ml-1 text-primary hover:underline",
          onClick: handleRedirectToBilling,
          children: "View billing"
        }), isGrowth && /*#__PURE__*/_jsx("a", {
          href: "#",
          className: "ml-1 text-primary hover:underline",
          onClick: e => {
            e.preventDefault();
            redirectToCheckout(GROWTH_SEAT.priceId);
          },
          children: "Downgrade plan"
        })]
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isStarter && 'bg-gray-200/50', ' border-b bg-muted p-6 text-left lg:order-[18] lg:border-b-0 lg:border-t'),
      children: [/*#__PURE__*/_jsx("div", {
        className: "hidden lg:block",
        children: /*#__PURE__*/_jsx("ul", {
          className: "mb-4 space-y-4",
          children: CREATOR_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
            className: "flex items-center gap-2",
            children: [/*#__PURE__*/_jsx(Check, {
              className: "size-4"
            }), /*#__PURE__*/_jsx("span", {
              children: feature
            })]
          }, index))
        })
      }), /*#__PURE__*/_jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "lg:hidden",
        children: /*#__PURE__*/_jsxs(AccordionItem, {
          value: "item-1",
          className: "border-b-0",
          children: [/*#__PURE__*/_jsx(AccordionTrigger, {
            children: "See what's included"
          }), /*#__PURE__*/_jsx(AccordionContent, {
            children: /*#__PURE__*/_jsx("ul", {
              className: "mb-4 space-y-4",
              children: CREATOR_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
                className: "flex items-center gap-2",
                children: [/*#__PURE__*/_jsx(Check, {
                  className: "size-4"
                }), /*#__PURE__*/_jsx("span", {
                  children: feature
                })]
              }, index))
            })
          })]
        })
      })]
    }), /*#__PURE__*/_jsx("p", {
      className: clsx(isGrowth && 'bg-gray-200/50', 'px-6 pb-2 pt-6 text-3xl mb-0 font-semibold lg:order-3'),
      children: GROWTH_SEAT.title
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isGrowth && 'bg-gray-200/50', 'p-6 lg:order-11'),
      children: [/*#__PURE__*/_jsxs("div", {
        className: "mb-4 flex justify-center",
        children: [/*#__PURE__*/_jsx("span", {
          className: "text-lg font-semibold",
          children: "$"
        }), /*#__PURE__*/_jsx("span", {
          className: "text-6xl font-semibold",
          children: GROWTH_SEAT.price
        })]
      }), /*#__PURE__*/_jsxs("p", {
        className: "text-sm text-muted-foreground",
        children: [formatNumber(GROWTH_SEAT.viewLimit), " views / month"]
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isGrowth && 'bg-gray-200/50', 'p-6 lg:order-[14]'),
      children: [/*#__PURE__*/_jsx(PricingButton, {
        hasActiveSession: Boolean(team),
        hasActiveSubscription: Boolean(team === null || team === void 0 ? void 0 : team.subscriptionActive),
        currentSubscriptionType: subscriptionType,
        subscriptionPlan: GROWTH_SEAT,
        redirectToCheckout: redirectToCheckout
      }), team && !isFree && /*#__PURE__*/_jsxs("p", {
        className: "mt-4 text-muted-foreground",
        children: [isGrowth && /*#__PURE__*/_jsx("a", {
          href: "#",
          className: "ml-1 text-primary hover:underline",
          onClick: handleRedirectToBilling,
          children: "View billing"
        }), isStarter && /*#__PURE__*/_jsx("a", {
          href: "#",
          className: "ml-1 text-primary hover:underline",
          onClick: e => {
            e.preventDefault();
            redirectToCheckout(GROWTH_SEAT.priceId);
          },
          children: "Upgrade plan"
        })]
      })]
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isGrowth && 'bg-gray-200/50', 'border-b p-6 text-left lg:order-[19] lg:border-b-0 lg:border-t'),
      children: [/*#__PURE__*/_jsx("div", {
        className: "hidden lg:block",
        children: /*#__PURE__*/_jsx("ul", {
          className: "mb-4 space-y-4",
          children: GROWTH_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
            className: "flex items-center gap-2",
            children: [/*#__PURE__*/_jsx(Check, {
              className: "size-4"
            }), /*#__PURE__*/_jsx("span", {
              children: feature
            })]
          }, index))
        })
      }), /*#__PURE__*/_jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "lg:hidden",
        children: /*#__PURE__*/_jsxs(AccordionItem, {
          value: "item-1",
          className: "border-b-0",
          children: [/*#__PURE__*/_jsx(AccordionTrigger, {
            children: "See what's included"
          }), /*#__PURE__*/_jsx(AccordionContent, {
            children: /*#__PURE__*/_jsx("ul", {
              className: "mb-4 space-y-4",
              children: GROWTH_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
                className: "flex items-center gap-2",
                children: [/*#__PURE__*/_jsx(Check, {
                  className: "size-4"
                }), /*#__PURE__*/_jsx("span", {
                  children: feature
                })]
              }, index))
            })
          })]
        })
      })]
    }), /*#__PURE__*/_jsx("p", {
      className: clsx(isEnterprise && 'bg-gray-200/50', 'px-6 pb-2 pt-6 text-3xl mb-0 font-semibold lg:order-4'),
      children: ENTERPRISE_SEAT.title
    }), /*#__PURE__*/_jsx("div", {
      className: clsx(isEnterprise && 'bg-gray-200/50', 'lg:order-12 mb-0')
    }), /*#__PURE__*/_jsx("div", {
      className: clsx(isEnterprise && 'bg-gray-200/50', 'p-6 lg:order-[14] mb-0'),
      children: /*#__PURE__*/_jsx(Link, {
        href: '/contact',
        className: clsx('w-full', buttonVariants({
          variant: 'outline'
        })),
        children: "Contact us"
      })
    }), /*#__PURE__*/_jsxs("div", {
      className: clsx(isEnterprise && 'bg-gray-200/50', 'p-6 text-left lg:order-[20] lg:border-t mb-0'),
      children: [/*#__PURE__*/_jsx("div", {
        className: "hidden lg:block",
        children: /*#__PURE__*/_jsx("ul", {
          className: "mb-4 space-y-4",
          children: ENTERPRISE_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
            className: "flex items-center gap-2",
            children: [/*#__PURE__*/_jsx(Check, {
              className: "size-4"
            }), /*#__PURE__*/_jsx("span", {
              children: feature
            })]
          }, index))
        })
      }), /*#__PURE__*/_jsx(Accordion, {
        type: "single",
        collapsible: true,
        className: "lg:hidden",
        children: /*#__PURE__*/_jsxs(AccordionItem, {
          value: "item-1",
          className: "border-b-0",
          children: [/*#__PURE__*/_jsx(AccordionTrigger, {
            children: "See what's included"
          }), /*#__PURE__*/_jsx(AccordionContent, {
            children: /*#__PURE__*/_jsx("ul", {
              className: "mb-4 space-y-4",
              children: ENTERPRISE_SEAT.usps.map((feature, index) => /*#__PURE__*/_jsxs("li", {
                className: "flex items-center gap-2",
                children: [/*#__PURE__*/_jsx(Check, {
                  className: "size-4"
                }), /*#__PURE__*/_jsx("span", {
                  children: feature
                })]
              }, index))
            })
          })]
        })
      })]
    })]
  });
};

export default PricingTable;